import {
    Outlet,
    RouteObject,
    RouterProvider,
    createBrowserRouter,
} from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { useAuth } from '@praxis/component-auth'
import { AnalyticsProvider } from '@praxis/analytics/client'
import type { EventAPIOptions } from '@praxis/analytics/types'

import Authenticate from 'pages/authenticate'
import { SalesRoute } from 'pages/sales'
import { HomePage } from 'pages'
import { SettingsRoute } from 'pages/settings'
import { LocationSearchRoute } from 'pages/location-search'
import { Unauthorized } from 'pages/unauthorized'
import { PayrollRoute } from 'pages/payroll'
import { ShortageRoute } from 'pages/shortage'
import { SafetyRoute } from 'pages/safety/routes'
import { MyTargetPerformanceRoute } from 'pages/myTarget-performance/routes'
import { StoreFactsRoute } from 'pages/store-facts'
import { NotesRoute } from 'pages/notes'
import { AssetsProtectionRoute } from 'pages/assets-protection/routes'
import { ItemSearchRoute } from 'pages/item-search/routes'
import { LoginPlaceholder } from 'components/login-placeholder/loginPlaceholder'
import { useEssentials } from 'hooks/utils/useEssentials'
import { UserDetails } from 'App'
import { LoyaltyRoute } from 'pages/loyalty'
import { InsightsRoute } from 'pages/insights'
import { TrailerRoute } from 'pages/trailer'
import { TrailerDetailRoute } from 'pages/trailer/routes'
import { OperationsRoute } from 'pages/operations'
import { Constants } from 'constants/constants'
import { useRestricted } from 'hooks/utils/useRestricted'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { UserDetailsData } from 'queries/getUserDetailsQuery'
import { BrandsRoute } from 'pages/brands/routes'
import { StoreFulfillmentRoute } from 'pages/fulfillment/store-fulfillment/routes'
import { useAutoPageTracking } from 'utils/analytics'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'
import { TRANSLATION_KEYS as keys } from 'locales/translation-keys'
import {
    UnauthorizedReason,
    type UnauthorizedReasonCode,
    useLogUnauthorizedReason,
} from 'utils/unauthorizedReason'
import { useDeviceInfo } from 'hooks/utils/useDeviceInfo'
import { PowerBIRoute } from 'pages/power-bi/routes'
import { useRouteAccess } from 'hooks/utils/useRouteAccess'
import { useScrollContainerRestoration } from 'hooks/utils/useScrollContainerRestoration'

const HomeRoute: RouteObject = {
    path: '/',
    index: true,
    element: <HomePage />,
}

/**
 * Ensures that the current user has the correct permissions (AD Groups) to
 * access a given route. If not, user will be shown the <Unauthorized /> page.
 *
 * The route to AD Group access mappings are configured in the public/config.json file.
 */
function AuthorizedRoutes() {
    let {
        env,
        pathname: path,
        currentLocation,
        greenfieldGroups: userPermissions,
    } = useEssentials()
    const { permissionsByKPI } = env

    // Auto-track page views (handles tracking automatically)
    useAutoPageTracking()

    const canAccessRoute = useRouteAccess()
    const isAuthorized = canAccessRoute(path)

    const { betaAccess, powerUserAccess, isZebraDevice } = useRestricted()
    const isAppRestricted = !(powerUserAccess || betaAccess || isZebraDevice)
    const isRoutePermissionDenied =
        !isAppRestricted && isAuthorized === false && userPermissions !== null

    let unauthorizedReason: UnauthorizedReasonCode | null = null
    if (isAppRestricted) {
        unauthorizedReason = UnauthorizedReason.APP_RESTRICTED
    } else if (isRoutePermissionDenied) {
        unauthorizedReason = UnauthorizedReason.ROUTE_PERMISSION_DENIED
    }

    useLogUnauthorizedReason(unauthorizedReason, {
        attempted_path: window.location.pathname,
        denied_route: path,
        denied_route_mapped: Boolean(permissionsByKPI?.[path]),
        requested_location: currentLocation,
        is_zebra: isZebraDevice,
        greenfield_groups_present: Boolean(userPermissions?.length),
    })

    if (isAppRestricted) {
        return (
            <Unauthorized
                message={Constants.APP_RESTRICTED}
                additionalMessage={
                    Constants.APP_RESTRICTED_ADDITIONAL_1 +
                    Constants.APP_RESTRICTED_ADDITIONAL_2
                }
                actionMessage={Constants.APP_RESTRICTED_ACTION}
            />
        )
    }

    // userPermissions is null when not yet loaded
    if (isAuthorized === false && userPermissions !== null) {
        return <Unauthorized />
    }
    if (isAuthorized) {
        return <Outlet />
    }
    return <LoginPlaceholder />
}

const AuthenticateRoute: RouteObject = {
    path: '/authenticate',
    element: <Authenticate />,
}

/**
 * The root route. Provides analytics and then loads the current route.
 * Must be called from within a router context.
 */
const Root = () => {
    const { env, session } = useEssentials()
    useScrollContainerRestoration()

    // Refs so the interceptor always reads the latest token/apiKey at
    // request-time without needing to re-register on every render.
    const accessTokenRef = useRef(session?.accessToken)
    const apiKeyRef = useRef(env.apiKey)
    accessTokenRef.current = session?.accessToken
    apiKeyRef.current = env.apiKey

    // Set up fetch interceptor for Firefly requests (compatible with OpenTelemetry).
    // Only registers once the token exists; dependency array ensures it runs
    // when the token first becomes available.
    useEffect(() => {
        if (!session?.accessToken) return

        class NewRequest extends Request {
            constructor(input: RequestInfo | URL, init?: RequestInit) {
                const url =
                    typeof input === 'string'
                        ? input
                        : input instanceof URL
                          ? input.href
                          : input.url

                // If this is a Firefly request, add authorization headers
                if (url.includes('firefly_internal_events')) {
                    super(input, {
                        ...init,
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${accessTokenRef.current}`,
                            'x-api-key': apiKeyRef.current,
                        },
                    })
                } else {
                    super(input, init)
                }
            }
        }

        window.Request = NewRequest
    }, [session?.accessToken])

    const hasToken = !!session?.accessToken

    const analyticsConfig: EventAPIOptions = {
        appName: env.firefly.appName as any,
        apiKey: env.apiKey,
        url: env.firefly.url,
        ...(hasToken && {
            fetch: (req: Request) => {
                req.headers.set(
                    'Authorization',
                    `Bearer ${session.accessToken}`,
                )
                return fetch(req)
            },
        }),
    }

    return (
        <AnalyticsProvider config={analyticsConfig} trackClicks={false}>
            <UserDetails>
                <AccessProvider>
                    <AuthorizedRoutes />
                </AccessProvider>
            </UserDetails>
        </AnalyticsProvider>
    )
}

export const AccessProvider = ({ children }: { children: React.ReactNode }) => {
    const { currentLocation, greenfieldGroups, userAgent, t } = useEssentials()
    const { isZebraDevice } = useDeviceInfo(userAgent)
    const queryClient = useQueryClient()
    const data = queryClient.getQueryData<UserDetailsData>([
        QueryKeys.USER_DETAILS,
    ])
    const queryState = queryClient.getQueryState([QueryKeys.USER_DETAILS])
    const isLoading = !queryState || queryState.status === 'loading'

    const locations = data?.locations
    const locationKeys = [
        'company_location_details',
        'region_location_details',
        'group_location_details',
        'district_location_details',
        'store_location_details',
    ] as const
    const combined = locations
        ? locationKeys.flatMap((key) => locations[key] ?? [])
        : []

    const match = combined.some((location) => {
        const matchShortName = location.short_name === currentLocation
        const matchName = location.name === currentLocation
        const matchCompany = currentLocation.includes(location.name)
        return matchShortName || matchName || matchCompany
    })

    const unauthorizedReason: UnauthorizedReasonCode | null =
        !isLoading && data && currentLocation && !match
            ? UnauthorizedReason.LOCATION_MISMATCH
            : null

    useLogUnauthorizedReason(unauthorizedReason, {
        attempted_path: window.location.pathname,
        requested_location: currentLocation,
        is_zebra: isZebraDevice,
        greenfield_groups_present: Boolean(greenfieldGroups?.length),
    })

    if (isLoading) {
        return <LoginPlaceholder />
    }

    // Unreachable in practice: UserDetails gates children on successful data.
    // Kept as a guard so a future refactor can't silently produce a hung spinner.
    if (!data) {
        return <Unauthorized message={t(keys.unauthorized.noUserDetailsData)} />
    }

    if (!match) return <Unauthorized />

    return <>{children}</>
}

/**
 * Whenever new route objects are created, they must be added as children here
 * or children of one of the routes given here.
 */
export const RootRoute: RouteObject = {
    path: '/',
    element: (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Root />
        </ErrorBoundary>
    ),
    children: [
        HomeRoute,
        ...InsightsRoute,
        SalesRoute,
        LocationSearchRoute,
        AuthenticateRoute,
        PayrollRoute,
        MyTargetPerformanceRoute,
        SettingsRoute,
        ShortageRoute,
        SafetyRoute,
        StoreFactsRoute,
        NotesRoute,
        AssetsProtectionRoute,
        ItemSearchRoute,
        LoyaltyRoute,
        TrailerRoute,
        TrailerDetailRoute,
        OperationsRoute,
        StoreFulfillmentRoute,
        PowerBIRoute,
        ...BrandsRoute,
    ],
}

const router = createBrowserRouter([RootRoute])

/**
 * Ensures login happens, if necessary. Then sets up the routing.
 */
const Router = () => {
    const { isAuthenticated, login } = useAuth()

    if (!isAuthenticated()) {
        login()
    }

    return (
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
    )
}

export default Router
